import { Component } from '@theme/component';
import {
  appendSubClassCartAudit,
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {Object} CartLineItem
 * @property {string} key - Item key
 * @property {number} id - Item ID
 * @property {number} variant_id - Variant ID
 * @property {number} quantity - Quantity
 * @property {Record<string, string>} [properties] - Item properties
 */

/**
 * @typedef {Object} CartData
 * @property {CartLineItem[]} items - Cart items
 * @property {number} item_count - Total item count
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isAdjustingFees = false;
  #publisherFeeCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    this.#fetchCartUpdateResponse({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    })
      .then(({ responseText, skipFeeAdjustment }) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        if (skipFeeAdjustment) {
          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          return;
        }

        // Handle digital fee adjustments after cart update
        this.#handleDigitalFeeAdjustments(parsedResponseText).then(() => {
          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Fetches cart update response with a combined fee adjustment when possible.
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.sections
   * @returns {Promise<{responseText: string, skipFeeAdjustment: boolean}>}
   */
  async #fetchCartUpdateResponse({ line, quantity, sections }) {
    const cartResponse = await fetch('/cart.js');
    const cartData = await cartResponse.json();
    const feeUpdate = await this.#buildFeeLineUpdates(line, quantity, cartData);

    if (feeUpdate) {
      const body = JSON.stringify({
        updates: feeUpdate,
        sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      return { responseText, skipFeeAdjustment: true };
    }

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections,
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const responseText = await response.text();
    return { responseText, skipFeeAdjustment: false };
  }

  /**
   * Build line updates for a digital product and matching fee line.
   * @param {number} line
   * @param {number} quantity
   * @param {any} cartData
   * @returns {Promise<Record<string, number> | null>}
   */
  async #buildFeeLineUpdates(line, quantity, cartData) {
    const currentItem = cartData?.items?.[line - 1];
    if (!currentItem || !this.#isDigitalProduct(currentItem)) {
      return null;
    }

    const currentRequiresFee = await this.#requiresDigitalFee(currentItem);
    if (!currentRequiresFee) {
      return null;
    }

    const feeItems = cartData.items.filter((/** @type {any} */ item) => this.#isDigitalFeeItem(item));
    if (feeItems.length === 0) {
      return null;
    }

    const delta = quantity - currentItem.quantity;
    const currentRequired = await this.#getApplicableDigitalCount(cartData);
    const feeTargetQuantity = Math.max(0, currentRequired + delta);

    /** @type {Record<string, number>} */
    const updates = {
      [currentItem.key]: quantity,
      [feeItems[0].key]: feeTargetQuantity,
    };

    feeItems.slice(1).forEach((/** @type {CartLineItem} */ item) => {
      updates[item.key] = 0;
    });

    return updates;
  }

  /**
   * Check if line item is a digital fee
   * @param {any} lineItem - Cart line item
   * @returns {boolean}
   */
  #isDigitalFeeItem(lineItem) {
    return lineItem.properties && lineItem.properties['_digital_delivery_fee'] === 'true';
  }

  /**
   * Check if line item is a digital product
   * @param {any} lineItem - Cart line item
   * @returns {boolean}
   */
  #isDigitalProduct(lineItem) {
    // Check if the item was marked as digital when added to cart
    return lineItem.properties && lineItem.properties['_is_digital'] === 'true';
  }

  /**
   * Check if a digital line item requires a digital fee.
   * @param {any} lineItem
   * @returns {Promise<boolean>}
   */
  async #requiresDigitalFee(lineItem) {
    const flag = lineItem.properties?._requires_digital_delivery_fee;
    if (flag === 'yes') return true;
    if (flag === 'no') return false;

    const publisherCode = lineItem.properties?._publisher_code;
    if (!publisherCode) return false;
    return this.#doesPublisherRequireFee(publisherCode);
  }

  /**
   * Handle digital fee adjustments after cart changes
   * @param {Object} cartData - Updated cart data
   */
  async #handleDigitalFeeAdjustments(cartData) {
    // Prevent concurrent fee adjustments
    if (this.#isAdjustingFees) {
      return;
    }
    
    this.#isAdjustingFees = true;
    
    try {
      // Get current cart state
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();

      const requiredFeeQuantity = await this.#getApplicableDigitalCount(currentCart);
      const feeItems = currentCart.items.filter((/** @type {any} */ item) => this.#isDigitalFeeItem(item));
      const existingFeeQuantity = feeItems.reduce((/** @type {number} */ sum, /** @type {CartLineItem} */ item) => sum + item.quantity, 0);

      if (feeItems.length > 0 && existingFeeQuantity !== requiredFeeQuantity) {
        await this.#updateFeeLines(feeItems, requiredFeeQuantity);
        await this.#refreshCartAfterFeeAdjustment();
        return;
      }

      if (feeItems.length === 0 && requiredFeeQuantity > 0) {
        await this.#addMissingDigitalFee(requiredFeeQuantity);
      }
    } catch (error) {
      console.error('Error adjusting digital fees:', error);
    } finally {
      this.#isAdjustingFees = false;
    }
  }

  /**
   * Determine whether a publisher requires a digital fee.
   * @param {string} publisherCode
   * @returns {Promise<boolean>}
   */
  async #doesPublisherRequireFee(publisherCode) {
    if (!publisherCode) return false;
    if (this.#publisherFeeCache.has(publisherCode)) {
      return this.#publisherFeeCache.get(publisherCode);
    }

    const api_url = window.digital_product_config?.api_url ?? '';
    const store_number = window.digital_product_config?.store_number ?? '';

    if (!api_url || !store_number) {
      this.#publisherFeeCache.set(publisherCode, false);
      return false;
    }

    try {
      const apiResponse = await fetch(`${api_url}?storeNumber=${store_number}&downloadprovidercode=${publisherCode}`);
      const result = await apiResponse.json();
      const isApplied = Boolean(result && result.length > 0 && result[0].isApplied);
      this.#publisherFeeCache.set(publisherCode, isApplied);
      return isApplied;
    } catch (error) {
      console.error(error);
      this.#publisherFeeCache.set(publisherCode, false);
      return false;
    }
  }

  /**
   * Get the total applicable digital count (fee applies).
   * @param {any} cartData
   * @returns {Promise<number>}
   */
  async #getApplicableDigitalCount(cartData) {
    const digitalItems = cartData.items.filter((/** @type {any} */ item) => this.#isDigitalProduct(item));
    const flaggedYes = digitalItems.filter(
      (/** @type {any} */ item) => item.properties?._requires_digital_delivery_fee === 'yes'
    );
    const flaggedNo = new Set(
      digitalItems
        .filter((/** @type {any} */ item) => item.properties?._requires_digital_delivery_fee === 'no')
        .map((/** @type {CartLineItem} */ item) => item.key)
    );
    const unflaggedItems = digitalItems.filter(
      (/** @type {any} */ item) => !('_requires_digital_delivery_fee' in (item.properties || {}))
    );

    const flaggedCount = flaggedYes.reduce((/** @type {number} */ sum, /** @type {CartLineItem} */ item) => sum + item.quantity, 0);
    if (unflaggedItems.length === 0) {
      return flaggedCount;
    }

    const digitalCounts = unflaggedItems.reduce((/** @type {Record<string, number>} */ acc, /** @type {any} */ item) => {
      if (flaggedNo.has(item.key)) return acc;
      const publisherCode = item.properties?._publisher_code;
      if (!publisherCode) return acc;
      acc[publisherCode] = (acc[publisherCode] || 0) + item.quantity;
      return acc;
    }, {});

    const publisherCodes = Object.keys(digitalCounts);
    if (publisherCodes.length === 0) {
      return flaggedCount;
    }

    const eligibility = await Promise.all(
      publisherCodes.map(async (publisherCode) => ({
        publisherCode,
        requiresFee: await this.#doesPublisherRequireFee(publisherCode),
      }))
    );

    const publisherCount = eligibility.reduce((/** @type {number} */ sum, /** @type {{publisherCode: string, requiresFee: boolean}} */ item) => {
      if (!item.requiresFee) return sum;
      return sum + (digitalCounts[item.publisherCode] || 0);
    }, 0);

    return flaggedCount + publisherCount;
  }

  /**
   * Update fee line items to a single target quantity.
   * @param {CartLineItem[]} feeItems
   * @param {number} targetQuantity
   */
  async #updateFeeLines(feeItems, targetQuantity) {
    /** @type {Record<string, number>} */
    const updates = {};
    feeItems.forEach((/** @type {CartLineItem} */ item, /** @type {number} */ index) => {
      updates[item.key] = index === 0 ? targetQuantity : 0;
    });

    await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
      }),
    });
  }
  /**
   * @param {string | number} variantId
   * @returns {Promise<any>}
   */
  async fetchMetafields(variantId) {
    if (typeof window !== 'undefined' && window.fetchMetafieldsOnce) {
      return window.fetchMetafieldsOnce(variantId);
    }

    const proxyPath = '/apps/shopifyData/getMetafields';
    const url = `${proxyPath}?query=gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Metafields');
      }
    } catch (error) {
      console.error('Error fetching Metafields:', error);
      throw error;
    }
  }
  /**
   * Add missing digital delivery fees
   * @param {number} quantity - Number of fees to add
   */
  async #addMissingDigitalFee(quantity) {
    try {
      const feeProductHandle = window.digital_product_config?.product_handle ?? ''
      
      // Get the fee product to find its variant ID
      const productResponse = await fetch(`/products/${feeProductHandle}.js`);
      if (!productResponse.ok) {
        console.error('Digital delivery fee product not found');
        return;
      }
      
      const feeProduct = await productResponse.json();
      const feeVariantId = feeProduct.variants[0]?.id;
      
      if (!feeVariantId) {
        console.error('No variant found for digital delivery fee product');
        return;
      }
      const metafields = await this.fetchMetafields(feeVariantId);
      /**
       * @param {string} key
       * @returns {string}
       */
      const getMetafieldValue = (key) => {
          const metafield = metafields.find((/** @type {{key: string, value: any}} */ m) => m.key === key);
          return metafield ? metafield.value : ''; 
      };

      const feeSubClass = getMetafieldValue('sub_class');
      // Add the fee product to cart
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: feeVariantId,
          quantity: quantity,
          properties: {
            '_digital_delivery_fee': 'true',
            '_is_digital': 'true',
            '_fee_type': 'online_store',
            '_department': getMetafieldValue('department'), 
            '_sub_department': getMetafieldValue('sub_department'), 
            '_class': getMetafieldValue('class'), 
            '_sub_class': feeSubClass
          }
        })
      });

      appendSubClassCartAudit(feeSubClass, 'component-cart-items.js', 541);
      
      // After adding fees, refresh the cart UI
      await this.#refreshCartAfterFeeAdjustment();
    } catch (error) {
      console.error('Failed to add missing digital fees:', error);
    }
  }


  /**
   * Refresh cart UI after fee adjustments
   */
  async #refreshCartAfterFeeAdjustment() {
    try {
      // Get fresh cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Dispatch cart update event to notify all components
      this.dispatchEvent(
        new CartUpdateEvent(cartData, this.sectionId, {
          itemCount: cartData.item_count,
          source: 'cart-items-component-fee-adjustment',
        })
      );
      
      // Refresh the cart sections
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      
      // Also dispatch the generic cart:update event for other listeners
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: this.sectionId,
            data: {
              itemCount: cartData.item_count,
              source: 'digital-fee-adjustment',
            },
          },
        })
      );
      
    } catch (error) {
      console.error('Failed to refresh cart after fee adjustment:', error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}